import React, { useEffect, useState, useRef } from 'react';
import Layout from '../../components/layout/Layout';
import { getData, postData } from '../../services/apiService';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import { useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
    Autocomplete, IconButton, Box, Checkbox, Pagination, MenuItem, Paper, Select,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
    Typography, FormControl, InputLabel, Button, Tabs, Tab, Alert,
} from "@mui/material";
import { showSuccess, showConfirmation, showError } from "../../hooks/swalAlerts";
import useFetchIp from "../../hooks/useFetchIp";
import Progress from '../../hooks/useProgressBar';
import useDebounce from '../../hooks/useDebounce';

const AssignCourseStudent = () => {
    const { ip } = useFetchIp();
    const isFetched = useRef(false);
    const isMobile = useMediaQuery('(max-width:600px)');
    const [assignmentMode, setAssignmentMode] = useState(0);
    const [courses, setCourses] = useState([]);
    const [groups, setGroups] = useState([]);
    const [colleges, setColleges] = useState([]);
    const [students, setStudents] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedCourse, setSelectedCourse] = useState("");
    const [selectedGroup, setSelectedGroup] = useState("");
    const [selectedCollege, setSelectedCollege] = useState("");
    const [loading, setLoading] = useState(false);
    const [groupAssigning, setGroupAssigning] = useState(false);
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [filterStatus, setFilterStatus] = useState('all');

    const selectedCourseEnrollment = courses.find(
        (c) => c.course?.courseName === selectedCourse
    );
    const selectedCourseId = selectedCourseEnrollment?.course?._id;

    useEffect(() => {
        if (!isFetched.current) {
            getColleges();
            isFetched.current = true;
        }
    }, []);

    useEffect(() => {
        if (selectedCollege) {
            loadInstituteData(selectedCollege);
        } else {
            setCourses([]);
            setGroups([]);
            setStudents([]);
            setSelectedCourse("");
            setSelectedGroup("");
        }
    }, [selectedCollege]);

    useEffect(() => {
        if (assignmentMode === 1 && selectedCollege && selectedCourseId) {
            getStudents();
        }
    }, [page, rowsPerPage, debouncedSearchQuery, selectedCourse, filterStatus, selectedGroup, selectedCollege, assignmentMode]);

    const loadInstituteData = async (collegeId) => {
        setLoading(true);
        setSelectedCourse("");
        setSelectedGroup("");
        setStudents([]);
        setPage(1);
        try {
            const [coursesRes, groupsRes] = await Promise.all([
                getData(`/admin/institute-courses/${collegeId}`),
                getData(`/admin/student-groups?college_id=${collegeId}`),
            ]);
            const instituteCourses = coursesRes || [];
            setCourses(instituteCourses);
            setGroups(groupsRes || []);
            if (instituteCourses.length > 0) {
                setSelectedCourse(instituteCourses[0].course?.courseName || "");
            }
        } catch (error) {
            console.error('Failed to fetch institute data', error);
            setCourses([]);
            setGroups([]);
        } finally {
            setLoading(false);
        }
    };

    const getColleges = async () => {
        try {
            const response = await getData('/get-colleges-for-dropdown');
            setColleges(response || []);
        } catch (error) {
            console.error('Failed to fetch colleges', error);
        }
    };

    const getStudents = async () => {
        if (!selectedCollege || !selectedCourseId) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                limit: String(rowsPerPage),
                search: debouncedSearchQuery,
                status: filterStatus,
                courseId: selectedCourseId,
                college_id: selectedCollege,
            });
            if (selectedGroup) params.append('group', selectedGroup);

            const response = await getData(`/admin/students-for-assignment?${params.toString()}`);
            if (response) {
                setTotalPages(response.totalPages || 1);
            }

            const studentsWithCheckedStatus = (response?.data || []).map((student) => {
                const isEnrolledInSelectedCourse = student.enrollments?.some(
                    (enrollment) =>
                        enrollment.course &&
                        enrollment.course.courseName === selectedCourse &&
                        enrollment.status === 'Active'
                );
                return { ...student, checked: isEnrolledInSelectedCourse };
            });
            setStudents(studentsWithCheckedStatus);
        } catch (error) {
            console.error('Failed to fetch students', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCollegeChange = (collegeId) => {
        setSelectedCollege(collegeId);
        setSearchQuery('');
        setFilterStatus('all');
    };

    const handleGroupAssign = async () => {
        if (!selectedCollege) {
            showError('Please select an institute first');
            return;
        }
        if (!selectedCourseId || !selectedGroup) {
            showError('Please select both a course and a student group');
            return;
        }

        const confirmed = await showConfirmation(
            `Assign "${selectedCourse}" to all students in group "${selectedGroup}"?`
        );
        if (!confirmed) return;

        setGroupAssigning(true);
        try {
            const response = await postData('/admin/assign-course-by-group', {
                courseId: selectedCourseId,
                group: selectedGroup,
                college_id: selectedCollege,
                ipAddress: ip,
            });
            if (response.message) {
                showSuccess(response.message);
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to assign course to group');
        } finally {
            setGroupAssigning(false);
        }
    };

    const handleCheckboxChange = async (_id) => {
        const confirmed = await showConfirmation("Are you sure?");
        if (!confirmed) return;

        try {
            const student = students.find((s) => s._id === _id);
            const newCheckedState = !student.checked;

            if (!selectedCourseId) {
                showError('Selected course not found');
                return;
            }

            if (newCheckedState) {
                const response = await postData('/admin/assign-course-students', {
                    courseId: selectedCourseId,
                    studentIds: [_id],
                    college_id: selectedCollege,
                    ipAddress: ip,
                });
                if (response.message) {
                    showSuccess(response.message);
                }
            } else {
                const response = await postData('/admin/unassign-course-student', {
                    courseId: selectedCourseId,
                    studentId: _id,
                    ipAddress: ip,
                });
                if (response.message) {
                    showSuccess(response.message);
                }
            }

            setStudents(students.map((s) =>
                s._id === _id ? { ...s, checked: newCheckedState } : s
            ));
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to update course assignment');
        }
    };

    const handleSelectAll = async () => {
        if (isAllSelected) return;
        const confirmed = await showConfirmation("Are you sure?");
        if (!confirmed) return;

        try {
            if (!selectedCourseId) {
                showError('Selected course not found');
                return;
            }

            const studentIds = students.map((student) => student._id);

            const response = await postData('/admin/assign-course-students', {
                courseId: selectedCourseId,
                studentIds,
                college_id: selectedCollege,
                ipAddress: ip,
            });
            if (response.message) {
                showSuccess(response.message);
            }

            setStudents(students.map((student) => ({ ...student, checked: true })));
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to update course assignments');
        }
    };

    const handlePageChange = (_event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(1);
    };

    const handleModeChange = (_event, newMode) => {
        setAssignmentMode(newMode);
        setPage(1);
    };

    const isAllSelected = students.length > 0 && students.every((student) => student.checked);
    const selectedCollegeName = colleges.find((c) => c._id === selectedCollege)?.collegeName;

    const selectSx = {
        height: "40px",
        borderRadius: "10px",
        boxShadow: "0px 0px 6px #00000014",
        ".MuiOutlinedInput-notchedOutline": { border: 0 },
    };

    const renderFilterSelect = (label, labelId, value, onChange, options, placeholder, disabled = false) => (
        <FormControl fullWidth disabled={disabled}>
            <InputLabel id={labelId}>{label}</InputLabel>
            <Select
                labelId={labelId}
                value={value}
                onChange={onChange}
                label={label}
                sx={selectSx}
                IconComponent={KeyboardArrowDownIcon}
                renderValue={(selected) => {
                    if (!selected) {
                        return <Typography color="text.secondary">{placeholder}</Typography>;
                    }
                    return selected;
                }}
            >
                {options}
            </Select>
        </FormControl>
    );

    const noInstituteCourses = selectedCollege && courses.length === 0 && !loading;

    return (
        <Layout>
            {loading && assignmentMode === 1 ? <Progress /> :
                <Box className="p-6 bg-gray-100 min-h-screen">
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>
                        Course Assignment
                    </Typography>

                    <Paper elevation={0} sx={{ p: 3, borderRadius: "10px", width: "100%" }}>
                        {/* <Box sx={{ mb: 3, maxWidth: isMobile ? '100%' : 400 }}>
                            {renderFilterSelect(
                                "Institute",
                                "institute-label",
                                selectedCollege,
                                (e) => handleCollegeChange(e.target.value),
                                colleges.map((college) => (
                                    <MenuItem key={college._id} value={college._id}>
                                        {college.collegeName}
                                    </MenuItem>
                                )),
                                "Select Institute"
                            )}
                        </Box> */}

                        <Box sx={{ mb: 3, maxWidth: isMobile ? '100%' : 400 }}>
                            <FormControl fullWidth>
                                <InputLabel id="institute-label">Institute</InputLabel>
                                <Select
                                    labelId="institute-label"
                                    value={selectedCollege}
                                    label="Institute"
                                    onChange={(e) => handleCollegeChange(e.target.value)}
                                    renderValue={(selected) => {
                                        const college = colleges.find(c => c._id === selected);
                                        return college ? college.collegeName : '';
                                    }}
                                >
                                    {colleges.map((college) => (
                                        <MenuItem key={college._id} value={college._id}>
                                            {college.collegeName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {!selectedCollege ? (
                            <Alert severity="info">
                                Please select an institute first. Only courses assigned to that institute can be assigned to students.
                            </Alert>
                        ) : (
                            <>
                                <Tabs
                                    value={assignmentMode}
                                    onChange={handleModeChange}
                                    sx={{
                                        mb: 3,
                                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                                        '& .Mui-selected': { color: '#4B49AC' },
                                        '& .MuiTabs-indicator': { backgroundColor: '#4B49AC' },
                                    }}
                                >
                                    <Tab label="Assign by Student Group" />
                                    <Tab label="Assign Individual Students" />
                                </Tabs>

                                {noInstituteCourses && (
                                    <Alert severity="warning" sx={{ mb: 3 }}>
                                        No courses are assigned to {selectedCollegeName}. Assign courses to this institute first via Assign Course.
                                    </Alert>
                                )}

                                {assignmentMode === 0 ? (
                                    <Box>
                                        <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 2, mb: 3 }}>
                                            <Box sx={{ flex: 1 }}>
                                                {renderFilterSelect(
                                                    "Course",
                                                    "group-course-label",
                                                    selectedCourse,
                                                    (e) => setSelectedCourse(e.target.value),
                                                    courses.map((enrollment) => (
                                                        <MenuItem key={enrollment._id} value={enrollment.course?.courseName}>
                                                            {enrollment.course?.courseName}
                                                        </MenuItem>
                                                    )),
                                                    "Select Course",
                                                    courses.length === 0
                                                )}
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                {renderFilterSelect(
                                                    "Student Group",
                                                    "group-filter-label",
                                                    selectedGroup,
                                                    (e) => setSelectedGroup(e.target.value),
                                                    groups.map((group) => (
                                                        <MenuItem key={group} value={group}>
                                                            {group}
                                                        </MenuItem>
                                                    )),
                                                    "Select Group",
                                                    groups.length === 0
                                                )}
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button variant="contained" onClick={handleGroupAssign}
                                                disabled={groupAssigning || !selectedCourse || !selectedGroup || courses.length === 0}
                                                sx={{
                                                    bgcolor: '#4B49AC',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    px: 4,
                                                    '&:hover': { bgcolor: '#3A3897' },
                                                }}
                                            >
                                                {groupAssigning ? 'Assigning...' : 'Assign to Group'}
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Box sx={{
                                            display: "flex",
                                            flexDirection: isMobile ? "column" : "row",
                                            justifyContent: "space-between",
                                            mb: 4,
                                            gap: 2,
                                            flexWrap: "wrap",
                                        }}>
                                            <Box sx={{ flex: 1, minWidth: 200 }}>
                                                <Box sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    width: "100%",
                                                    border: "1px solid #e2e2e2",
                                                    borderRadius: 1,
                                                    bgcolor: "#fdfdfd",
                                                    p: 0.5,
                                                }}>
                                                    <SearchIcon sx={{ ml: 0.5, color: "text.secondary", fontSize: 20 }} />
                                                    <Autocomplete
                                                        freeSolo
                                                        options={[]}
                                                        fullWidth
                                                        value={searchQuery}
                                                        onInputChange={(_event, newValue) => {
                                                            setSearchQuery(newValue);
                                                            setPage(1);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                placeholder="Search students"
                                                                variant="standard"
                                                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                                                sx={{ ml: 0.3 }}
                                                            />
                                                        )}
                                                    />
                                                </Box>
                                            </Box>

                                            <Box sx={{ flex: 1, minWidth: 180 }}>
                                                {renderFilterSelect(
                                                    "Course",
                                                    "course-label",
                                                    selectedCourse,
                                                    (e) => { setSelectedCourse(e.target.value); setPage(1); },
                                                    courses.map((enrollment) => (
                                                        <MenuItem key={enrollment._id} value={enrollment.course?.courseName}>
                                                            {enrollment.course?.courseName}
                                                        </MenuItem>
                                                    )),
                                                    "Course",
                                                    courses.length === 0
                                                )}
                                            </Box>

                                            <Box sx={{ flex: 1, minWidth: 180 }}>
                                                {renderFilterSelect(
                                                    "Student Group",
                                                    "group-label",
                                                    selectedGroup,
                                                    (e) => { setSelectedGroup(e.target.value); setPage(1); },
                                                    [
                                                        <MenuItem key="all" value="">All Groups</MenuItem>,
                                                        ...groups.map((group) => (
                                                            <MenuItem key={group} value={group}>{group}</MenuItem>
                                                        )),
                                                    ],
                                                    "All Groups"
                                                )}
                                            </Box>

                                            <Box sx={{ flex: 1, minWidth: 150 }}>
                                                {renderFilterSelect(
                                                    "Status",
                                                    "status-label",
                                                    filterStatus,
                                                    (e) => { setFilterStatus(e.target.value); setPage(1); },
                                                    [
                                                        <MenuItem key="all" value="all">All</MenuItem>,
                                                        <MenuItem key="assigned" value="assigned">Assigned</MenuItem>,
                                                        <MenuItem key="unassigned" value="unassigned">Unassigned</MenuItem>,
                                                    ],
                                                    "Status"
                                                )}
                                            </Box>
                                        </Box>

                                        <TableContainer component={Paper} elevation={0} sx={{ mb: 4, overflowX: "auto" }}>
                                            <Table sx={{ minWidth: isMobile ? 550 : "auto" }}>
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: "#3A3897", borderRadius: "10px 10px 0 0" }}>
                                                        <TableCell align="center" sx={{ color: "white", width: "8%" }}>No</TableCell>
                                                        <TableCell align="center" sx={{ color: "white", width: "14%" }}>EMP Id</TableCell>
                                                        <TableCell align="center" sx={{ color: "white", width: "28%" }}>Student</TableCell>
                                                        <TableCell align="center" sx={{ color: "white", width: "18%" }}>Group</TableCell>
                                                        <TableCell align="center" sx={{ color: "white", width: "18%" }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <Checkbox
                                                                    checked={isAllSelected}
                                                                    onChange={handleSelectAll}
                                                                    disabled={isAllSelected || courses.length === 0}
                                                                    sx={{
                                                                        color: 'white',
                                                                        '&.Mui-checked': { color: 'white' },
                                                                    }}
                                                                />
                                                                <Typography sx={{ color: 'white', ml: 1 }}>Assign All</Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {students.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                                <Typography color="text.secondary">No students found</Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        students.map((student, index) => (
                                                            <TableRow
                                                                key={student._id}
                                                                sx={{ "&:last-child td, &:last-child th": { border: 0 }, border: "1px solid #ededed" }}
                                                            >
                                                                <TableCell align="center">
                                                                    {(page - 1) * rowsPerPage + index + 1}
                                                                </TableCell>
                                                                <TableCell align="center">{student.employee_id}</TableCell>
                                                                <TableCell align="center">
                                                                    {student.firstName} {student.lastName}
                                                                </TableCell>
                                                                <TableCell align="center">{student.group || '—'}</TableCell>
                                                                <TableCell align="center">
                                                                    {student.checked ? (
                                                                        <IconButton color="error" onClick={() => handleCheckboxChange(student._id)}>
                                                                            <RemoveIcon />
                                                                        </IconButton>
                                                                    ) : (
                                                                        <IconButton color="primary" sx={{ color: '#3A3897' }}
                                                                            onClick={() => handleCheckboxChange(student._id)}
                                                                            disabled={courses.length === 0}
                                                                        >
                                                                            <AddIcon />
                                                                        </IconButton>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                                            <Pagination
                                                count={totalPages}
                                                page={page}
                                                onChange={handlePageChange}
                                                shape="rounded"
                                                sx={{
                                                    '& .MuiPaginationItem-root': {
                                                        '&.Mui-selected': { bgcolor: '#4B49AC', color: 'white' },
                                                    },
                                                }}
                                            />
                                            <Select
                                                value={rowsPerPage}
                                                onChange={handleRowsPerPageChange}
                                                size="small"
                                                sx={{
                                                    ml: 2,
                                                    height: 35,
                                                    bgcolor: '#f7f7f7',
                                                    border: '1px solid #e2e2e2',
                                                    '& .MuiSelect-select': { py: 0.75 },
                                                }}
                                                IconComponent={KeyboardArrowDownIcon}
                                            >
                                                <MenuItem value={10}>10 / page</MenuItem>
                                                <MenuItem value={20}>20 / page</MenuItem>
                                                <MenuItem value={50}>50 / page</MenuItem>
                                                <MenuItem value={100}>100 / page</MenuItem>
                                            </Select>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </Paper>
                </Box>
            }
        </Layout>
    );
};

export default AssignCourseStudent;
